import React from 'react';
import {
    CodeIcon,
    ImageIcon,
    Link2Icon,
    VideoIcon,
    UploadIcon,
    StackIcon,
    Cross2Icon,
    OpenInNewWindowIcon,
} from '@radix-ui/react-icons';
import styles from './AddLeadCampaign.module.css';
import { MAX_IMAGES, TARGET_ACTIONS } from './campaignOptions';
import { assetUrl, looksLikeUrl, isVideoUrl, isEmbedUrl, embedPoster } from './campaignFormUtils';

// The creative itself: either a list of media items (uploads, URLs, library
// picks), each with its own redirect, or a block of raw HTML. All state lives in
// the parent form; this renders it and reports changes back.
const CreativeSection = ({
    creativeMode,
    setCreativeMode,
    creativeHtml,
    onHtmlChange,
    creativeItems,
    uploading,
    onImageUpload,
    onVideoUpload,
    linkKind,
    setLinkKind,
    linkValue,
    setLinkValue,
    onSubmitLink,
    onOpenLibrary,
    onSetItemRedirect,
    onRemoveItem,
}) => {
    const atLimit = creativeItems.length >= MAX_IMAGES;

    return (
        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
            <div className={styles.labelRow}>
                <label className={styles.label}>Creative <span className={styles.required}>*</span></label>
                <div className={styles.modeToggle}>
                    <button type="button"
                        className={creativeMode === 'image' ? styles.modeBtnActive : styles.modeBtn}
                        onClick={() => setCreativeMode('image')}>
                        {/* Label reads "Media" — this mode holds images, video and embeds.
                            The internal value stays 'image' so no other logic changes. */}
                        <ImageIcon /> Media
                    </button>
                    <button type="button"
                        className={creativeMode === 'html' ? styles.modeBtnActive : styles.modeBtn}
                        onClick={() => setCreativeMode('html')}>
                        <CodeIcon /> HTML
                    </button>
                </div>
            </div>

            {creativeMode === 'image' ? (
                <div className={styles.creativeBox}>
                    <div className={styles.uploadRow}>
                        {/* Grouped by source: upload, URL, library. */}
                        <div className={styles.uploadGroup}>
                            <span className={styles.groupLabel}>Upload</span>
                            <div className={styles.groupBtns}>
                                <label className={styles.uploadBtn}>
                                    <UploadIcon /> {uploading ? 'Uploading…' : 'Image'}
                                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onImageUpload} disabled={uploading || atLimit} hidden />
                                </label>
                                <label className={styles.uploadBtn}>
                                    <UploadIcon /> {uploading ? 'Uploading…' : 'Video'}
                                    <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onVideoUpload} disabled={uploading || atLimit} hidden />
                                </label>
                            </div>
                        </div>
                        <div className={styles.uploadGroup}>
                            <span className={styles.groupLabel}>Add by URL</span>
                            <div className={styles.groupBtns}>
                                <button type="button" className={styles.libraryBtn} onClick={() => { setLinkKind(linkKind === 'video' ? null : 'video'); setLinkValue(''); }} disabled={atLimit}>
                                    <Link2Icon /> Video URL
                                </button>
                                <button type="button" className={styles.libraryBtn} onClick={() => { setLinkKind(linkKind === 'youtube' ? null : 'youtube'); setLinkValue(''); }} disabled={atLimit}>
                                    <VideoIcon /> YouTube / Vimeo
                                </button>
                            </div>
                        </div>
                        <div className={styles.uploadGroup}>
                            <span className={styles.groupLabel}>Library</span>
                            <div className={styles.groupBtns}>
                                <button type="button" className={styles.libraryBtn} onClick={onOpenLibrary} disabled={atLimit}>
                                    <StackIcon /> Choose from library
                                </button>
                            </div>
                        </div>
                        <span className={styles.hint}>
                            {creativeItems.length}/{MAX_IMAGES} items · each can have its own redirect. Multiple images = carousel.
                        </span>
                    </div>

                    {linkKind && (
                        <div className={styles.redirectRow}>
                            <input
                                type="url"
                                value={linkValue}
                                onChange={(e) => setLinkValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmitLink(); } }}
                                placeholder={linkKind === 'video'
                                    ? 'https://…/video.mp4  (direct video file)'
                                    : 'https://youtube.com/watch?v=…  or  vimeo.com/…'}
                                className={styles.input}
                                autoFocus
                            />
                            <button type="button" className={styles.libraryBtn} onClick={onSubmitLink} disabled={uploading || !linkValue.trim()}>
                                {uploading ? 'Adding…' : 'Add'}
                            </button>
                        </div>
                    )}

                    {creativeItems.length === 0 ? (
                        <div className={styles.emptyImages}>No media yet — upload, add a URL, or choose from the library.</div>
                    ) : (
                        <ul className={styles.imageList}>
                            {creativeItems.map((it, idx) => (
                                <li key={`${it.asset_id}-${idx}`} className={styles.imageRow}>
                                    {isEmbedUrl(it.file_url) ? (
                                        embedPoster(it.file_url)
                                            ? <img src={embedPoster(it.file_url)} alt="" className={styles.thumb} />
                                            : <span className={styles.thumb} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><VideoIcon /></span>
                                    ) : isVideoUrl(it.file_url) ? (
                                        <video src={assetUrl(it.file_url)} className={styles.thumb} muted playsInline preload="metadata" />
                                    ) : (
                                        <img src={assetUrl(it.file_url)} alt={it.alt_text || ''} className={styles.thumb} />
                                    )}
                                    <div className={styles.imageFields}>
                                        <div className={styles.redirectRow}>
                                            <input
                                                type="text"
                                                value={it.redirect_url}
                                                onChange={(e) => onSetItemRedirect(idx, e.target.value)}
                                                placeholder="Redirect: https://…  or  open_contact_form"
                                                className={`${styles.input} ${looksLikeUrl(it.redirect_url) ? styles.linkInput : ''}`}
                                                list="actionOptions"
                                            />
                                            {looksLikeUrl(it.redirect_url) && (
                                                <a
                                                    href={it.redirect_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.openLinkBtn}
                                                    title="Open link in a new tab"
                                                >
                                                    <OpenInNewWindowIcon />
                                                </a>
                                            )}
                                        </div>
                                        <span className={styles.hintSmall}>Leave empty to use the campaign's Target Action.</span>
                                    </div>
                                    <button type="button" className={styles.removeBtn} onClick={() => onRemoveItem(idx)} title="Remove from this campaign"><Cross2Icon /></button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : (
                <div className={styles.creativeBox}>
                    <textarea
                        name="creative_html"
                        value={creativeHtml}
                        onChange={onHtmlChange}
                        placeholder='Paste HTML here…  e.g. <div style="...">Offer</div>'
                        className={`${styles.input} ${styles.codeArea}`}
                        style={{ minHeight: '150px' }}
                    />
                    <span className={styles.hint}>Use inline styles — external CSS/fonts won't load in the app's WebView.</span>
                </div>
            )}
            {/* Suggested keyword actions for the per-image redirect fields. */}
            <datalist id="actionOptions">
                {TARGET_ACTIONS.map((a) => <option key={a} value={a} />)}
            </datalist>
        </div>
    );
};

export default CreativeSection;
